import { useTranslations } from "@zapier/i18n";
import {
  HydratedInterface,
  Member,
  useInterface,
  useInterfaceActionPermissions,
  useUpdateInterfaceConfig,
} from "@zapier/client";
import { Icon } from "@zapier/zinnia-icons";
import { Button, SkeletonText } from "@zapier/design-system";
import {
  ShareModal,
  useCopyToClipboard,
  useEmitChangeOwnerInteractionEvent,
  useEmitShareInteractionEvent,
} from "@zapier/shared";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { EmbedSettings } from "./EmbedSettings";
import { buildEmbedSnippet, SnippetType } from "./embedSnippet";

interface InterfaceShareModalProps {
  currentInterface: HydratedInterface | undefined;
  isOpen: boolean;
  onCloseModal: () => void;
  onChangeOwner: (newOwner: Member) => void;
}

export interface EmbedSettingsForm {
  allowedDomains: string;
  allowQueryParameters: boolean;
  frameHeight: string;
  frameWidth: string;
  includeBackground: boolean;
  pageId: string;
  restrictDomains: boolean;
  snippetType: SnippetType;
}

const embedFormDefaults: EmbedSettingsForm = {
  allowedDomains: "",
  allowQueryParameters: false,
  frameHeight: "500px",
  frameWidth: "900px",
  includeBackground: true,
  pageId: "",
  restrictDomains: false,
  snippetType: "script",
};

function DefaultFooter({
  isLoading,
  publishedUrl,
}: {
  isLoading: boolean;
  publishedUrl: string;
}) {
  const t = useTranslations("interface-share-modal");

  const copyToClipboard = useCopyToClipboard();

  if (isLoading) {
    return <SkeletonText lines={1} width="20ch" />;
  }

  return (
    <Button onClick={() => copyToClipboard(publishedUrl)} variant="ghost">
      <Icon name="actionLink" />
      {t("buttons.copy-view-only-link")}
    </Button>
  );
}

function EmbedFooter({
  isUpdating,
  onSubmit,
  onCopyEmbed,
}: {
  isUpdating: boolean;
  onSubmit: () => void;
  onCopyEmbed: () => void;
}) {
  const t = useTranslations("interface-share-modal");

  return (
    <>
      <Button
        isPending={isUpdating}
        onClick={onSubmit}
        type="submit"
        variant="primary"
      >
        {t("buttons.done")}
      </Button>

      <Button onClick={onCopyEmbed} variant="tertiary">
        <Icon name="arrowCode" />
        {t("buttons.copy-embed")}
      </Button>
    </>
  );
}

export function InterfaceShareModal({
  currentInterface,
  isOpen,
  onChangeOwner,
  onCloseModal,
}: InterfaceShareModalProps) {
  const t = useTranslations("interface-share-modal");
  const { emit } = useEmitShareInteractionEvent();
  const { emit: emitChangeOwner } = useEmitChangeOwnerInteractionEvent();
  const { canChangeOwner, enablePermissionsCheck, canShare } =
    useInterfaceActionPermissions(currentInterface?.id || "");

  const [currentTab, setCurrentTab] = useState("default");

  const copyToClipboard = useCopyToClipboard();

  const { isInterfaceLoading, loginPageId, publishedUrl } = useInterface({
    id: currentInterface?.id ?? "",
    enabled: Boolean(currentInterface && isOpen),
  });

  useEffect(() => {
    if (isOpen) {
      enablePermissionsCheck();
    }
  }, [isOpen, enablePermissionsCheck]);

  const {
    mutateAsync: updateInterfaceConfig,
    isPending: isUpdatingInterfaceConfig,
  } = useUpdateInterfaceConfig();

  const embedForm = useForm<EmbedSettingsForm>({
    defaultValues: embedFormDefaults,
    mode: "onTouched",
  });

  const onEmbedSubmit = embedForm.handleSubmit((data: EmbedSettingsForm) => {
    const embedHosts = data.restrictDomains
      ? data.allowedDomains.split(",").map((host) => host.trim())
      : [];

    updateInterfaceConfig({
      id: currentInterface?.id ?? "",
      body: { embedHosts },
    });
  });

  const footerMap = {
    default: (
      <DefaultFooter
        isLoading={isInterfaceLoading}
        publishedUrl={publishedUrl}
      />
    ),
    embed: (
      <EmbedFooter
        isUpdating={isUpdatingInterfaceConfig}
        onSubmit={async () => {
          await onEmbedSubmit();
          onCloseModal();
        }}
        onCopyEmbed={async () => {
          const embedFormValues = embedForm.getValues();
          const snippet = buildEmbedSnippet(embedFormValues);
          await copyToClipboard(snippet);
        }}
      />
    ),
  };

  return (
    <ShareModal
      allowSharingWithEveryone={true}
      allowSharingWithTeams={true}
      allowSharingWithAdmins={true}
      assetId={currentInterface?.id ?? ""}
      assetName={currentInterface?.name ?? ""}
      assetOwner={currentInterface?.owner}
      assetType="interface"
      canChangeOwner={canChangeOwner}
      canShare={canShare}
      currentTabName={t("tabs.editors")}
      emit={(subjectId: string) =>
        emit({
          optionSelected: "share_interface",
          assetId: currentInterface?.id,
          subjectId,
        })
      }
      extraTabs={{
        embed: {
          body: currentInterface ? (
            <FormProvider {...embedForm}>
              <form onSubmit={onEmbedSubmit}>
                <EmbedSettings
                  interfaceAsset={{ id: currentInterface.id, loginPageId }}
                />
              </form>
            </FormProvider>
          ) : undefined,
          text: t("tabs.embed"),
        },
      }}
      footerContent={footerMap[currentTab] || footerMap.default}
      isOpen={isOpen}
      onChangeOwner={(newOwner) => {
        onChangeOwner(newOwner);
        emitChangeOwner({
          assetId: currentInterface?.id ?? "",
          subjectId: String(newOwner.id),
        });
      }}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCloseModal();
        }
      }}
      onTabChange={(tabKey) => setCurrentTab(tabKey)}
      roles={["editor"]}
    />
  );
}
